import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import SVG from 'components/base/SVG';
import { addListener, removeListener, isOnElement } from 'utils/DOM/onOuterClick';

// The Search header's own clear control draws this asset rather than the
// sprite's `iconClose`, so the two read as one control across the app.
import clearSearch from '../../../assets/images/clearSearch.svg';
// Figma 291:5588 — the recent-search row's own X is a 2px round-cap stroke,
// not the sprite's filled glyph, so it ships as its own asset.
import clearRecentSearch from '../../../assets/images/clearRecentSearch.svg';

import css from './style.scss';

// Subject-property input with its suggestion / recent-search dropdown —
// Figma nodes 291:9023 (closed) and 452:33479 (recent searches open).
//
// The shared components/SuggestedInput is not used here: the design needs a
// per-row remove control and a "Clear Recent Searches" footer inside the
// panel, which that component's react-autosuggest list cannot render without
// forking it. Suggestion fetching still goes through the same endpoint.
const SubjectInput = ({
  label,
  suggestions,
  suggestionsQuery,
  recentSubjects,
  preselectedId,
  onFetch,
  onSelect,
  onSubmit,
  onClear,
  onRemoveRecent,
  onClearRecents,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleOuterClick = (ev) => {
      if (!isOnElement(ev, ref.current)) setOpen(false);
    };

    addListener(handleOuterClick);

    return () => removeListener(handleOuterClick);
  }, [open]);

  // A lone suggestion is preselected upstream (investors reducer): the row is
  // highlighted here, but the typed text and the open panel are left as-is so
  // the user can keep typing or pick something else.
  //
  // With nothing typed the panel offers the recent subjects instead.
  const showRecent = !(label || '').length;
  const items = showRecent ? recentSubjects : suggestions;
  // Empty only once the response for the text on screen has landed — not mid-typing or debounce.
  const noResults = !showRecent && !items.length && suggestionsQuery === label;
  const hasPanel = open && (Boolean(items.length) || noResults);

  const handleSelect = (item) => () => {
    onSelect(item);
    setOpen(false);
  };

  // Enter in the field submits like the Find Investor button (ATAI-451). Rows
  // are buttons, so Enter on a focused row still picks it natively; this only
  // fires from the input itself. Skipped mid-IME composition.
  const handleKeyDown = (ev) => {
    if (ev.key !== 'Enter' || ev.nativeEvent.isComposing) return;
    ev.preventDefault();
    onSubmit();
  };

  return (
    <div className={css.subject} ref={ref}>
      <div className={classNames(css.subject__field, { [css.subject__fieldOpen]: hasPanel })}>
        <input
          className={css.subject__input}
          value={label || ''}
          placeholder="Enter Property Address"
          onChange={(ev) => onFetch(ev.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {!(label || '').length ? null : (
          <button type="button" className={css.subject__clear} aria-label="Clear" onClick={onClear}>
            <img src={clearSearch} alt="Close" className={css.subject__clearIcon} />
          </button>
        )}
      </div>

      {!hasPanel ? null : (
        <div className={css.subject__panel}>
          <div className={css.subject__list}>
            {!showRecent ? null : <p className={css.subject__caption}>Recent Searches</p>}
            {!noResults ? null : <p className={css.subject__empty}>No data</p>}
            {items.map((item) => (
              <div
                className={classNames(css.subject__item, {
                  [css.subject__itemSelected]: !showRecent && item.id === preselectedId,
                })}
                key={item.key || item.label}
              >
                <button
                  type="button"
                  className={css.subject__itemButton}
                  onClick={handleSelect(item)}
                >
                  {/* `iconClockRecent` is the design's own clock, added for this
                      panel — the sprite's older `iconClock` draws a thinner
                      stroke in the wrong grey. `iconLocationDot` was already
                      exact. */}
                  <SVG
                    icon={showRecent ? 'iconClockRecent' : 'iconLocationDot'}
                    className={css.subject__itemIcon}
                  />
                  <span className={css.subject__itemLabel}>{item.label}</span>
                </button>
                {!showRecent ? null : (
                  <button
                    type="button"
                    className={css.subject__remove}
                    aria-label={`Remove ${item.label}`}
                    onClick={() => onRemoveRecent(item.key)}
                  >
                    <img src={clearRecentSearch} alt="" className={css.subject__removeIcon} />
                  </button>
                )}
              </div>
            ))}
            {!showRecent ? null : (
              <>
                <div className={css.subject__separator} />
                <button type="button" className={css.subject__clearAll} onClick={onClearRecents}>
                  Clear Recent Searches
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectInput;
